import { Component, computed, Inject, inject, OnInit, signal, DOCUMENT } from '@angular/core';
import { EditEventType, StorageBin, Technician, Warehouse } from '../_models';
import { TechnicianService } from '../_services/technician.service';
import { GridReadyEvent, GetRowIdParams } from 'ag-grid-community';
import { AgGridGenericLinkRenderer } from '../_components/_shared/ag-grid-renderer/ag-grid-generic-link.component';
import { AgGridStorageBinActionsRenderer } from '../_components/_shared/ag-grid-renderer/ag-grid-storage-bins-actions.component';
import { CommonModule, Location } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

import { ActivatedRoute, Router, RouterModule } from '@angular/router'
import { GridApi, ColDef, RowClassRules, StartEditingCellParams } from 'ag-grid-enterprise'
import { WarehouseService } from '../_services'

import { AG_GRID_LOCALE_DE } from 'src/app/_helpers/aggrid.locale.de'
import { EditInputComponent } from '../_components/_shared/edit-input/edit-input.component';
import { AgGridAngular } from 'ag-grid-angular';
import { NgxMapboxGLModule } from 'ngx-mapbox-gl';
import { AgGridThemeService } from '../_services/ag-grid-theme.service';
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { TooltipModule } from "@cloudfactorydk/ng2-tooltip-directive";

@Component({
  selector: 'hawk-warehouse-show',
  templateUrl: './warehouse-show.component.html',
  styleUrls: ['./warehouse-show.component.scss'],
  imports: [RouterModule, CommonModule, FormsModule, FontAwesomeModule, EditInputComponent, AgGridAngular, NgxMapboxGLModule, TooltipModule],
  host: {
    class: 'app-page'
  }
})
export class WarehouseShowComponent implements OnInit {
  private gridApi!: GridApi;
  public hawkTheme = inject(AgGridThemeService).getTheme();
  public locale = AG_GRID_LOCALE_DE
  public warehouseId?: string
  public warehouse: Warehouse;
  public markerData?: { lat: number, lng: number, color: string, text: string }[]
  public position?: [number, number]
  public technicians: Technician[] = []
  public storageBins?: StorageBin[]
  public addressFirstName?: string;
  public addressLastName?: string;
  public addressZip?: string | string;
  public addressCity?: string | string;
  public addressCountry?: string | string;
  public addressState?: string | string;
  public editTelephone: boolean = false;
  public editTechnician: boolean = false;
  public editMainWarehouse: boolean = false;
  public editFreeFill: boolean = false;
  public editUniStockId: boolean = false;
  public editLabel: boolean = false;
  public editLimitTemplate1: boolean = false;
  public editLimitTemplate2: boolean = false;
  public editLimitTemplate3: boolean = false;
  public editCompany: boolean = false;
  public editName: boolean = false;
  public editStreet: boolean = false;
  public editZipcode: boolean = false;
  public editCountry: boolean = false;
  // Dialog für Versand-E-Mails
  readonly notificationEmailsDialogOpen = signal(false);
  notificationEmailInputs: string[] = [];
  notificationEmailErrors: boolean[] = [];
  public isAddingStorage: boolean = false;
  public isRowEditing: boolean = false;
  public canDelete: boolean = false;
  public editingRowIndex?: number;
  public rowSelection: "single" | "multiple" = "multiple"
  public editType: "fullRow" = "fullRow"
  public rowClassRules: RowClassRules = {
    // if tech on site, color row
    'tech-on-site': (params) => {
      if (params?.data === undefined) return "...";
      return params.data.tech_on_site
    },
  }

  public defaultColDef: ColDef = {
    filter: true,
    flex: 1,
    editable: true,
    enableCellChangeFlash: true
  }

  public getRowId(params: GetRowIdParams) {
    return params.data.id;
  }

  // Column Definitions: Defines the columns to be displayed.
  public colDefs: ColDef[] = [
    {
      field: "name",
      headerName: '',
      valueSetter: (params) => {
        if(params.newValue && params.newValue !== '' && params.newValue !== undefined) {
          params.data.name = params.newValue;
          return true;
        }
        return false;
      },
      cellRenderer: AgGridGenericLinkRenderer,
      cellRendererParams: {
        routerLink: ['/', 'lager', '@warehouse_id', 'lagerort', '@id', 'alle'],
        labelFromData: true,
        dataKey: 'name',
        // icon: ['fa', 'fa-cubes']
      }
    },
    {
      field: "articles_count",
      headerName: this.locale.warehouseArticle,
      editable: false,
      cellRenderer: AgGridGenericLinkRenderer,
      cellRendererParams: {
        routerLink: ['/', 'lager', '@warehouse_id', 'lagerort', '@id', 'articles'],
        labelFromData: true,
        dataKey: 'articles_count',
        icon: ['fa', 'fa-cube']
      },
      minWidth: 80,
      width: 80,
      maxWidth: 100
    },
    {
      field: "bad_parts_count",
      headerName: this.locale.warehouseBadParts,
      editable: false,
      cellRenderer: AgGridGenericLinkRenderer,
      cellRendererParams: {
        routerLink: ['/', 'lager', '@warehouse_id', 'lagerort', '@id', 'bad-parts'],
        labelFromData: true,
        dataKey: 'bad_parts_count',
        icon: ['fa', 'fa-bug']
      },
      minWidth: 105,
      width: 105,
      maxWidth: 120
    },
    {
      field: "is_full",
      headerName: 'Full',
      editable: false,
      cellRenderer: AgGridGenericLinkRenderer,
      cellRendererParams: {
        onClickFunc: (data?: any) => {
          return this.updateBinFull(data);
        },
        label: null,
        dataKey: 'is_full',
        iconFunc: (data: any) => {
          if(data.is_full) {
            return ['fa', 'fa-battery-full'];
          } else if (data.articles_count <= 0) {
            return ['fa', 'fa-battery-empty']
          } else {
            return ['fa', 'fa-battery-quarter']
          }
        }
      },
      minWidth: 80,
      width: 80,
      maxWidth: 100
    },
    {
      field: 'actions',
      headerName: '',
      editable: false,
      sortable: false,
      filter: false,
      cellRenderer: AgGridStorageBinActionsRenderer,
      cellRendererParams: {
        onEditFunc: (params?: StartEditingCellParams) => {
          this.gridApi.startEditingCell({ rowIndex: params.rowIndex, colKey: 'name' });
        },
        onDeleteFunc: (data?: any) => {
          return this.onDeleteStorageBin(data);
        }
      }
    }
  ]

  downloadingStocktakeExcel = false;
  stocktakeLinkCopied = signal(false);
  private stocktakeCopyTimer?: any;

  // Bin-Auswahl-Dialog für gefilterten Inventur-Link
  readonly binSelectDialogOpen = signal(false);
  readonly selectedBinIds = signal<Set<number>>(new Set());
  readonly filteredLinkCopied = signal(false);
  private filteredCopyTimer?: any;

  readonly filteredStocktakeUrl = computed(() => {
    const ids = this.selectedBinIds();
    if (ids.size === 0) return '';
    const baseUrl = this.getTechnicianUrlAbs();
    if (!baseUrl) return '';
    return `${baseUrl}?bins=${Array.from(ids).join(',')}`;
  });

  readonly startStocktakeDlgOpen = signal(false);
  readonly extendStocktakeDlgOpen = signal(false);

  // Start-Dialog State
  readonly startStocktakeMode = signal<'days'|'date'>('days');
  readonly startStocktakeDays = signal<number>(7);
  readonly startStocktakeDateLocal = signal<string>(this.nextWeekEndLocal());

  // Verlängern-Dialog State
  readonly extendStocktakeDateLocal = signal<string>('');


  constructor(
    private warehouseService: WarehouseService,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private technicianService: TechnicianService,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  ngOnInit(): void {
    this.warehouseId = this.route.snapshot.paramMap.get('id') || "0"
    this.markerData = []
    this.addressFirstName = ''
    this.addressLastName = ''
    this.loadTechnicians()
    this.loadWareHouse()
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api!;
    this.warehouseService.loadStorageBins(this.warehouseId).subscribe(
      {
        next: (storageBins:StorageBin[]) => {
          this.storageBins = storageBins;
          this.canDelete = storageBins.length <= 0;
        },
        error: (error) => {
          console.log('error', error);
        }
      }
    )
  }

  private updateBinFull(data: any) {
    this.warehouseService.updateStorageBin(
      this.warehouseId,
      data.id,
      {
        binIsFull: data.is_full ? null : true
      }
    ).subscribe(
      {
        next: (storageBin: StorageBin) => {
          const rowNode = this.gridApi.getRowNode(`${data.id}`);
          if(rowNode) {
            rowNode.setData({
              ...data,
              is_full: storageBin.is_full
            });
          }
        },
        error: (error) => {
          console.log('error', error);
        }
      }
    )
  }

  private loadTechnicians() {
    this.technicianService.getAll().subscribe({
      next: (data) => {
        this.technicians = data
      },
      error: (err) => {
        console.error(err)
      }
    })
  }

  private loadWareHouse() {
    this.warehouseService.loadWarehouse(this.warehouseId).subscribe(
      {
        next: (data) => {
          this.updateData(data);
        },
        error: (error) => {
          console.log('error', error);
        }
      }
    )
  }

  public addStorage() {
    this.isAddingStorage = true;
    this.gridApi.applyTransactionAsync({add: [{
      articles_count: 0,
      bad_parts_count: 0,
      is_full: false,
      warehouse_id: this.warehouseId,
      new_record: true
    }]}, (event) => {
      this.editingRowIndex = event.add[0].rowIndex;
      this.isRowEditing = true;
      this.gridApi.startEditingCell({
        rowIndex: this.editingRowIndex,
        colKey: 'name'
      });
      // this.isAddingStorage = false;
    })
  }

  private stopAdding() {
    this.isAddingStorage = false;
  }
public onCellValueChanged(event: any) {
  console.log('on cell value changed', event);

  const data = event.data;
  const warehouseId = this.warehouseId;

  if (event.colDef.field === 'name') {
    this.gridApi.showLoadingOverlay();

    let service = null;
    const isNewRecord = !!data.new_record;

    if (isNewRecord) {
      service = this.warehouseService.addNewStorageBin(this.warehouseId, { label: data.name });
    } else if (data.id && parseInt(data.id) > 0) {
      service = this.warehouseService.updateStorageBin(this.warehouseId, data.id, { label: data.name });
    }

    if (!service) {
      this.gridApi.hideOverlay();
      this.stopAdding();
      return;
    }

    service.subscribe({
      next: (response: any) => {
        if (isNewRecord) {
          event.node.setData({
            ...response,
            new_record: false
          });
        }
        this.gridApi.hideOverlay();
        this.stopAdding();
      },
      error: (error: any) => {
        if (isNewRecord) {
          this.gridApi.applyTransactionAsync({remove: [{
            articles_count: 0,
            bad_parts_count: 0,
            is_full: false,
            warehouse_id: warehouseId,
            new_record: true
          }]});
        } else {
          if (error && error.data) {
            event.node.setData({
              ...error.data
            });
          } else {
            alert(`Fehler beim Speichern des Lagerplatzes: ${error.message || 'Unbekannter Fehler'}`);
          }
        }
        this.gridApi.hideOverlay();
        this.stopAdding();
      }
    });
  }
}

  public onRowEditingStarted(event) {
    console.log('on editing rowEditingStarted', event);
  }

  public onDeleteStorageBin(data: any) {
    if(data.new_record) return;
    if(data.bad_parts_count > 0 || data.articles_count > 0) return;
    if(confirm('Are you sure you want to delete item?')) {
      this.gridApi.showLoadingOverlay();
      this.warehouseService.deleteStorageBin(this.warehouseId, data.id).subscribe(
        {
          next: () => {
            this.gridApi.applyTransactionAsync({remove: [data.id]});
            this.gridApi.hideOverlay();
          },
          error: (error: any) => {
            this.gridApi.hideOverlay();
            alert(`${error.message}`);
          }
        }
      )
    }
  }

  public onRowEditingStopped(event: any) {
    console.log('on editing rowEditingStopped', event);
    if(event.data.new_record && (!event.data.name || event.data.name === '' || event.data.name === undefined)) {
      this.gridApi.applyTransactionAsync({remove: [{
        articles_count: 0,
        bad_parts_count: 0,
        is_full: false,
        warehouse_id: this.warehouseId,
        new_record: true
      }]});
      this.isAddingStorage = false;
    }
  }

  public saveTelephone(event: EditEventType) {
    this.metaUpdate('telephone', event);
    this.editTelephone = false;
  }

  public saveTechnician(event: EditEventType) {
    this.metaUpdate('owner_id', event);
    this.editTechnician = false;
  }

  public saveMainWarehouse(event: EditEventType) {
    this.metaUpdate('main_warehouse', event === true);
    this.editMainWarehouse = false;
  }

  public saveFreeFill(event: EditEventType) {
    this.metaUpdate('freefill', event === true);
    this.editFreeFill = false;
  }

  public saveUniStockId(event: EditEventType) {
    this.metaUpdate('uni_stock_id', event);
    this.editUniStockId = false;
  }

  public saveLabel(event: EditEventType) {
    this.metaUpdate('label', event);
    this.editLabel = false;
  }

  public saveLimitTemplate1(event: EditEventType) {
    this.metaUpdate('limit_template1', event);
    this.editLimitTemplate1 = false;
  }

  public saveLimitTemplate2(event: EditEventType) {
    this.metaUpdate('limit_template2', event);
    this.editLimitTemplate2 = false;
  }

  public saveLimitTemplate3(event: EditEventType) {
    this.metaUpdate('limit_template3', event);
    this.editLimitTemplate3 = false;
  }

  public saveCompany(event: EditEventType) {
    this.metaUpdate('address_company', event);
    this.editCompany = false;
  }

  public saveStreet(event: EditEventType) {
    this.metaUpdate('address_street', event);
    this.editStreet = false;
  }

  public updateStocktakeDate(event: string) {
    this.metaUpdate('stocktake_valid_until', event)
  }

  public saveName() {
    const name = { address_firstname: this.addressFirstName || '', address_lastname: this.addressLastName || '' };
    this.metaUpdate(null, null, name);
    this.editName = false;
  }

  public saveZipcode() {
    this.metaUpdate(null, null, { address_zip: this.addressZip, address_city: this.addressCity });
    this.editZipcode = false;
  }

  public deleteStocktake() {
    this.metaUpdate(null, null, { stocktake_secret: null, stocktake_valid_until: null })
  }

  public saveCountry() {
    this.metaUpdate(null, null, { address_country: this.addressCountry, address_state: this.addressState });
    this.editCountry = false;
  }

  openNotificationEmailsDialog() {
    // Bestehende Mails aus DB-String parsen
    const raw = this.warehouse?.delivery_notification_emails || '';
    const emails = raw.split(/[,;]/).map(e => e.trim()).filter(e => e.length > 0);
    // Mindestens ein leeres Feld anzeigen
    this.notificationEmailInputs = emails.length > 0 ? [...emails] : [''];
    this.notificationEmailErrors = this.notificationEmailInputs.map(() => false);
    this.notificationEmailsDialogOpen.set(true);
  }

  addNotificationEmailInput() {
    this.notificationEmailInputs.push('');
    this.notificationEmailErrors.push(false);
  }

  removeNotificationEmailInput(index: number) {
    this.notificationEmailInputs.splice(index, 1);
    this.notificationEmailErrors.splice(index, 1);
  }

  validateNotificationEmail(index: number) {
    const email = this.notificationEmailInputs[index].trim();
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    this.notificationEmailErrors[index] = email.length > 0 && !pattern.test(email);
  }

  get canSaveNotificationEmails(): boolean {
    // Keine Fehler und keine leere Eingabe mit Fehler
    return !this.notificationEmailErrors.some(e => e);
  }

  saveNotificationEmails() {
    // Alle validieren
    this.notificationEmailInputs.forEach((_, i) => this.validateNotificationEmail(i));
    if (!this.canSaveNotificationEmails) return;

    // Leere Einträge filtern, als komma-getrennten String speichern
    const emails = this.notificationEmailInputs.map(e => e.trim()).filter(e => e.length > 0);
    const value = emails.join(', ');
    this.metaUpdate('delivery_notification_emails', value || null);
    this.notificationEmailsDialogOpen.set(false);
  }

  public toggleLabel() {
    this.editLabel = !this.editLabel;
  }

  public onDelete() {
    if(this.storageBins.length > 0) return;

    if(confirm('Möchten Sie das Lager wirklich deaktivieren?')) {
      this.warehouseService.deactivateWarehouse(this.warehouseId).subscribe(
        {
          next: () => {
            this.router.navigate(['lager', 'uebersicht']);
          },
          error: (error) => {
            alert(error?.message || 'Something wrong went');
          }
        }
      )
    }
    return false;
  }

  private metaUpdate(key:string|null = null, value:any = null, data = {}) {
    const changeData = key === null ? data : {}

    if(key !== null)
      changeData[key] = value

    if (!this.warehouseId || this.warehouseId === "0") {
      return;
    }

    this.warehouseService.updateWareHouse(this.warehouseId, changeData).subscribe(
      {
        next: (data) => {
          this.updateData(data)
        },
        error: (error) => {
          console.log('error', error);
        }
      }
    )
  }

  private updateData(data: Warehouse) {
    this.warehouse = data;
    this.addressFirstName = data.address_firstname;
    this.addressLastName = data.address_lastname;
    this.addressCity = data.address_city;
    this.addressZip = data.address_zip;
    this.addressCountry = data.address_country;
    this.addressState = data.address_state;
    if (this.markerData) {
      this.markerData.push({
        lat: data.address_latitude,
        lng: data.address_longitude,
        color: '#af0000',
        text: 'Store'
      });
    }
    this.position = [data.address_longitude, data.address_latitude]
  }

  public startStocktake(): void {
    this.warehouseService.startStocktake(this.warehouse.id, { valid_days: 7 }).subscribe(
      data => {
        this.updateData(data)
      },
      error => {
        console.log(error);
      }
    )
  }

  private filenameFromDisposition(dispo?: string | null, fallback = 'inventur.xlsx') {
    if (!dispo) return fallback;
    // Content-Disposition: attachment; filename="foo.xlsx"; filename*=UTF-8''foo.xlsx
    const fnStar = dispo.match(/filename\*\=([^']*)''([^;]+)/i)?.[2];
    if (fnStar) return decodeURIComponent(fnStar);
    const fn = dispo.match(/filename="?([^"]+)"?/i)?.[1];
    return fn || fallback;
  }

  downloadExcel(wh: Warehouse) {
    this.downloadingStocktakeExcel = true;
    this.warehouseService.downloadStocktakeExcel(wh.id).subscribe({
      next: (res) => {
        const blob = res.body!;
        const dispo = res.headers.get('Content-Disposition');
        const filename = this.filenameFromDisposition(dispo, `inventur_${wh.id}.xlsx`);

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        this.downloadingStocktakeExcel = false;
      },
      error: (err) => {
        console.error('Excel-Download fehlgeschlagen', err);
        alert('Konnte die Excel nicht herunterladen.');
        this.downloadingStocktakeExcel = false;
      }
    });
  }

  getTechnicianUrlAbs(): string {
    if (!this.warehouse?.id || !this.warehouse?.stocktake_secret) return '';

    // 1) Router-URL bauen (ohne Domain)
    const tree = this.router.createUrlTree([
      '/lager', this.warehouse.id, 'inventur', this.warehouse.stocktake_secret, 'start'
    ]);
    const relative = this.router.serializeUrl(tree);        // z.B. "/lager/123/inventur/XYZ/start"

    // 2) Base-Href anwenden (fügt "/a/" o.ä. voran)
    const withBase = this.location.prepareExternalUrl(relative); // z.B. "/a/lager/123/inventur/XYZ/start"

    // 3) Absolut machen
    const origin = this.document.location.origin;           // z.B. "https://app.example.com"
    return origin + withBase;                               // → "https://app.example.com/a/lager/123/…"
  }

  async copyStocktakeTechnicianLink() {
    const url = this.getTechnicianUrlAbs();
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback ohne Clipboard-API
        const ta = document.createElement('textarea');
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      this.stocktakeLinkCopied.set(true);
      clearTimeout(this.stocktakeCopyTimer);
      this.stocktakeCopyTimer = setTimeout(() => this.stocktakeLinkCopied.set(false), 2000);
    } catch (e) {
      console.error('Copy failed', e);
      alert('Link konnte nicht kopiert werden.');
    }
  }

  openBinSelectDialog() {
    this.selectedBinIds.set(new Set());
    this.filteredLinkCopied.set(false);
    this.binSelectDialogOpen.set(true);
  }

  toggleBinSelection(binId: number) {
    const current = new Set(this.selectedBinIds());
    if (current.has(binId)) {
      current.delete(binId);
    } else {
      current.add(binId);
    }
    this.selectedBinIds.set(current);
  }

  async copyFilteredStocktakeLink() {
    const ids = this.selectedBinIds();
    if (ids.size === 0) return;

    const baseUrl = this.getTechnicianUrlAbs();
    if (!baseUrl) return;

    const binsParam = Array.from(ids).join(',');
    const url = `${baseUrl}?bins=${binsParam}`;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement('textarea');
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      this.filteredLinkCopied.set(true);
      clearTimeout(this.filteredCopyTimer);
      this.filteredCopyTimer = setTimeout(() => this.filteredLinkCopied.set(false), 2000);
    } catch (e) {
      console.error('Copy failed', e);
      alert('Link konnte nicht kopiert werden.');
    }
  }

  confirmDeleteStocktake(): void {
    if (confirm('Möchten Sie die aktuelle Inventur wirklich löschen? Der aktuelle Techniker-Link verliert dann seine Gültigkeit.')) {
      this.deleteStocktake()
    }
  }

  // Helpers: Local <input type="datetime-local"> Werte
  private pad(n: number) { return n.toString().padStart(2, '0'); }

  private toLocalInput(dt: Date) {
    // yyyy-MM-ddTHH:mm (ohne Sekunden)
    const y = dt.getFullYear();
    const m = this.pad(dt.getMonth()+1);
    const d = this.pad(dt.getDate());
    const hh = this.pad(dt.getHours());
    const mm = this.pad(dt.getMinutes());
    return `${y}-${m}-${d}T${hh}:${mm}`;
  }

  private nextWeekEndLocal() {
    const now = new Date();
    const end = new Date(now.getTime() + 7*24*60*60*1000);
    // 23:59 lokaler Zeit
    end.setHours(23,59,0,0);
    return this.toLocalInput(end);
  }

  // Öffner
  openStartStocktakeDialog() {
    this.startStocktakeMode.set('days');
    this.startStocktakeDays.set(7);
    this.startStocktakeDateLocal.set(this.nextWeekEndLocal());
    this.startStocktakeDlgOpen.set(true);
  }

  openExtendStocktakeDialog() {
    // Vorbelegen mit aktuellem Wert oder +7 Tage
    const cur = this.warehouse?.stocktake_valid_until
      ? new Date(this.warehouse.stocktake_valid_until)
      : new Date(Date.now() + 7*24*60*60*1000);
    this.extendStocktakeDateLocal.set(this.toLocalInput(cur));
    this.extendStocktakeDlgOpen.set(true);
  }

  // Aktionen
  confirmStartStocktake() {
    // Variante A: Tage → direkt schicken
    if (this.startStocktakeMode() === 'days') {
      const days = Math.max(1, Math.floor(this.startStocktakeDays() || 0));
      this.warehouseService.startStocktake(this.warehouse.id, { valid_days: days }).subscribe({
        next: (data) => { this.updateData(data); this.startStocktakeDlgOpen.set(false); },
        error: (err) => { console.error(err); alert('Start fehlgeschlagen'); }
      });
      return;
    }

    // Variante B: fixes Enddatum → Tage-Differenz berechnen (ceil)
    const sel = this.startStocktakeDateLocal();
    if (!sel) { alert('Bitte ein Enddatum wählen'); return; }
    const now = new Date();
    const end = new Date(sel);
    const ms = end.getTime() - now.getTime();
    const days = Math.max(1, Math.ceil(ms / (24*60*60*1000)));

    this.warehouseService.startStocktake(this.warehouse.id, { valid_days: days }).subscribe({
      next: (data) => { this.updateData(data); this.startStocktakeDlgOpen.set(false); },
      error: (err) => { console.error(err); alert('Start fehlgeschlagen'); }
    });
  }

  confirmExtendStocktake() {
    const sel = this.extendStocktakeDateLocal();
    if (!sel) { alert('Bitte ein neues Enddatum wählen'); return; }
    const end = new Date(sel);
    if (isNaN(end.getTime())) { alert('Ungültiges Datum'); return; }
    if (end.getTime() <= Date.now()) { alert('Datum muss in der Zukunft liegen'); return; }

    // ISO ins Backend (Rails erwartet String)
    this.updateStocktakeDate(end.toISOString());
    this.extendStocktakeDlgOpen.set(false);
  }

  protected readonly Date = Date;
  protected readonly faInfoCircle = faInfoCircle;
}
